/**
 * Resolve a declared request body against a call's context.
 *
 * The vocabulary is deliberately small — see the header of
 * `common/model/logic/optimisticLogicData.js` for what a spec looks like:
 *
 *   '${item.itemName}'   read a path from the context
 *   'a/${item.id}/b'     a placeholder with text around it interpolates, because
 *                        a path is text — this is how an endpoint is declared
 *   '${uuid.reservation}' a fresh id, stable within one call per name
 *   { when: '…', … }      an array element that is dropped when `when` is falsy
 *
 * A string with no `${…}` at all is left alone, so a literal stays a literal.
 * A string that is entirely one placeholder resolves to the value at that path,
 * not to its text — a quantity stays a number and a missing field stays null
 * rather than becoming the string "undefined". Only the third form, a
 * placeholder with text around it, produces a string, because there is nothing
 * else it could produce.
 */

const PLACEHOLDER = /^\$\{([^}]+)\}$/
const EMBEDDED = /\$\{([^}]+)\}/g

const readPath = (root, path) =>
    path.split('.').reduce((v, k) => (v == null ? undefined : v[k]), root)

/**
 * @param spec  the declared body — or any string with placeholders in it,
 *              which is how an endpoint is resolved by the same call
 * @param ctx   { item, record, input, selection } — the work item, its record,
 *              what the acting section supplied, and — for an action driven by
 *              a list rather than a line — the row currently selected. A process
 *              view has one work item; a matching modal has two rows in play at
 *              once, and `item` stays the one the action is about (33.2 step 9).
 * @param newId () => string, so the caller decides where ids come from
 */
const resolveParams = (spec, ctx = {}, newId = () => globalThis.crypto?.randomUUID()) => {
    const ids = new Map()
    const uuidFor = (name) => {
        if (!ids.has(name)) ids.set(name, newId())
        return ids.get(name)
    }

    const resolve = (value) => {
        if (Array.isArray(value)) {
            return value
                .filter(el => !(el && typeof el === 'object' && 'when' in el) || !!resolve(el.when))
                .map(el => {
                    if (el && typeof el === 'object' && 'when' in el) {
                        const { when, ...rest } = el
                        return resolve(rest)
                    }
                    return resolve(el)
                })
        }

        if (value && typeof value === 'object') {
            return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, resolve(v)]))
        }

        if (typeof value !== 'string') return value

        const lookup = (path) => path.startsWith('uuid.')
            ? uuidFor(path.slice(5))
            : readPath(ctx, path)

        const whole = PLACEHOLDER.exec(value)
        if (whole) {
            const resolved = lookup(whole[1])
            return resolved === undefined ? null : resolved
        }

        // A placeholder with text around it can only *be* text — an endpoint
        // rather than a value — so this branch interpolates instead of
        // resolving. It is the one case where the rule above cannot apply, and
        // it exists because the alternative was a second templating dialect for
        // request paths (33.2 step 4). A missing segment becomes '' so the
        // result is a visibly wrong URL rather than one containing the word
        // "undefined".
        if (!value.includes('${')) return value
        return value.replace(EMBEDDED, (_, path) => {
            const resolved = lookup(path)
            return resolved === undefined || resolved === null ? '' : String(resolved)
        })
    }

    return resolve(spec ?? {})
}

export default resolveParams
